import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Copy, Check, FileText, ExternalLink, MoreVertical, Trash2 } from 'lucide-react';
import { useForms, useDeleteForm } from '../hooks/useApi';
import { formatDate } from '../lib/utils';
import { cn } from '../lib/utils';

export default function FormsPage() {
  const navigate = useNavigate();
  const { data, isLoading } = useForms();
  const deleteForm = useDeleteForm();
  const [copied, setCopied] = useState<string | null>(null);
  const [menuOpen, setMenuOpen] = useState<string | null>(null);

  const copyUrl = (url: string, id: string) => {
    navigator.clipboard.writeText(url);
    setCopied(id);
    setTimeout(() => setCopied(null), 2000);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('WARNING: Deleting this form will also delete all submissions and applicants that came through it. This cannot be undone.\n\nAre you sure you want to delete this form?')) {
      await deleteForm.mutateAsync(id);
    }
    setMenuOpen(null);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'draft':
        return 'bg-yellow-100 text-yellow-800';
      case 'closed':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  const forms = data?.forms || [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Application Forms</h2>
          <p className="text-gray-600 mt-1">
            Create and manage custom application forms for your job listings
          </p>
        </div>
        <button
          onClick={() => navigate('/forms/new')}
          className="btn-primary inline-flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Create Form
        </button>
      </div>

      {forms.length === 0 ? (
        <div className="card p-12 text-center">
          <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No forms yet</h3>
          <p className="text-gray-500 mb-6">
            Create your first application form to start collecting submissions.
          </p>
          <button
            onClick={() => navigate('/forms/new')}
            className="btn-primary inline-flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Create Form
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {forms.map((form) => (
            <div key={form.id} className="card p-5 hover:shadow-md transition-shadow relative">
              {/* Menu */}
              <div className="absolute top-4 right-4">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setMenuOpen(menuOpen === form.id ? null : form.id);
                  }}
                  className="p-1 text-gray-400 hover:text-gray-600 rounded"
                >
                  <MoreVertical className="w-4 h-4" />
                </button>
                {menuOpen === form.id && (
                  <>
                    <div
                      className="fixed inset-0 z-10"
                      onClick={() => setMenuOpen(null)}
                    />
                    <div className="absolute right-0 mt-1 w-40 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20">
                      <button
                        onClick={() => {
                          navigate(`/forms/${form.id}`);
                          setMenuOpen(null);
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                      >
                        Edit Form
                      </button>
                      <button
                        onClick={() => {
                          navigate(`/forms/${form.id}/submissions`);
                          setMenuOpen(null);
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                      >
                        View Submissions
                      </button>
                      <button
                        onClick={() => handleDelete(form.id)}
                        className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                      >
                        <span className="inline-flex items-center gap-2">
                          <Trash2 className="w-3.5 h-3.5" />
                          Delete
                        </span>
                      </button>
                    </div>
                  </>
                )}
              </div>

              <div
                className="cursor-pointer"
                onClick={() => navigate(`/forms/${form.id}`)}
              >
                <div className="flex items-start gap-3 mb-3 pr-8">
                  <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <FileText className="w-5 h-5 text-primary-600" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-medium text-gray-900 truncate">{form.name}</h3>
                    <p className="text-sm text-gray-500 truncate">{form.job_title}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3 mb-4">
                  <span
                    className={cn(
                      'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize',
                      getStatusColor(form.status)
                    )}
                  >
                    {form.status}
                  </span>
                  <span className="text-sm text-gray-500">
                    {form.submission_count} submission{form.submission_count !== 1 ? 's' : ''}
                  </span>
                </div>
              </div>

              {/* Public URL */}
              <div className="flex items-center gap-2 pt-3 border-t border-gray-100">
                <input
                  type="text"
                  value={form.public_url}
                  readOnly
                  className="flex-1 text-xs text-gray-500 bg-gray-50 rounded px-2 py-1.5 truncate"
                />
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    copyUrl(form.public_url, form.id);
                  }}
                  className="p-1.5 text-gray-400 hover:text-gray-600 rounded"
                  title="Copy URL"
                >
                  {copied === form.id ? (
                    <Check className="w-4 h-4 text-green-600" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </button>
                <a
                  href={form.public_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="p-1.5 text-gray-400 hover:text-gray-600 rounded"
                  title="Open form"
                >
                  <ExternalLink className="w-4 h-4" />
                </a>
              </div>

              <p className="text-xs text-gray-400 mt-2">
                Created {formatDate(form.created_at)}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
